import { useMemo } from 'react'
import { useSearchParams } from 'react-router'

function readValues (params, key) {
  try {
    const parsed = JSON.parse(params.get(key) || '[]')

    return Array.isArray(parsed)
      ? [...new Set(parsed.filter(value => typeof value === 'string'))]
      : []
  } catch {
    return []
  }
}

export function useRegisterFilters ({
  namespace,
  items,
  definitions,
  getSearchText
}) {
  const [searchParams, setSearchParams] = useSearchParams()
  const keyFor = key => `${namespace}.${key}`

  const state = useMemo(() => {
    const query = searchParams.get(`${namespace}.q`) || ''

    const values = Object.fromEntries(
      definitions.map(field => [
        field.key,
        readValues(searchParams, `${namespace}.${field.key}`)
      ])
    )

    const options = Object.fromEntries(
      definitions.map(field => [
        field.key,
        [
          ...new Set([...items.flatMap(field.values), ...values[field.key]])
        ].sort((a, b) =>
          a.localeCompare(b, undefined, {
            numeric: true,
            sensitivity: 'base'
          })
        )
      ])
    )

    const search = query.trim().toLowerCase()

    const filtered = items.filter(item => {
      if (search && !getSearchText(item).toLowerCase().includes(search)) {
        return false
      }

      return definitions.every(field => {
        const selected = values[field.key]

        return (
          selected.length === 0 ||
          selected.some(value => field.values(item).includes(value))
        )
      })
    })

    return { query, values, options, filtered }
  }, [searchParams, namespace, items, definitions, getSearchText])

  function setQuery (query) {
    setSearchParams(
      previous => {
        const next = new URLSearchParams(previous)

        if (query) next.set(keyFor('q'), query)
        else next.delete(keyFor('q'))

        return next
      },
      { replace: true }
    )
  }

  function toggle (fieldKey, value) {
    if (!definitions.some(field => field.key === fieldKey)) return

    setSearchParams(previous => {
      const next = new URLSearchParams(previous)
      const key = keyFor(fieldKey)
      const selected = readValues(next, key)

      const updated = selected.includes(value)
        ? selected.filter(item => item !== value)
        : [...selected, value]

      if (updated.length) next.set(key, JSON.stringify(updated))
      else next.delete(key)

      return next
    })
  }

  function clear () {
    setSearchParams(previous => {
      const next = new URLSearchParams(previous)

      next.delete(keyFor('q'))

      definitions.forEach(field => {
        next.delete(keyFor(field.key))
      })

      return next
    })
  }

  return {
    ...state,
    definitions,
    total: items.length,
    setQuery,
    toggle,
    clear
  }
}
